import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import axiosClient from "../../api/axiosClient";
import { Link } from "react-router-dom";

type ApiNotification = {
  notification_id: number;
  type: string;
  message: string;
  created_at: string;
  updated_at?: string;
  is_read: 0 | 1;
};

type ApiResponse = {
  status_filter?: "all" | "read" | "unread";
  total_notifications?: number;
  read_count?: number;
  unread_count?: number;
  filtered_count?: number;
  notifications: ApiNotification[];
  status?: string;
  count?: number;
};

function timeAgo(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000); // seconds

  if (diff < 5) return "just now";
  if (diff < 60) return `${diff} sec${diff > 1 ? "s" : ""} ago`;
  const diffMin = Math.floor(diff / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} hour${diffH > 1 ? "s" : ""} ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} day${diffD > 1 ? "s" : ""} ago`;
  return date.toISOString().split("T")[0];
}

function typeIcon(type: string) {
  switch ((type || "").toLowerCase()) {
    case "profile":
      return "mdi:account-circle";
    case "payment":
    case "payment received":
      return "mdi:cash";
    case "document":
      return "mdi:file-upload";
    case "system":
    case "alert":
      return "mdi:alert-circle";
    default:
      return "mdi:bell-outline";
  }
}

export default function NotificationsPage() {
  const [statusFilter, setStatusFilter] = useState<"all" | "unread" | "read">("all");
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState<number | null>(null);
  const mounted = useRef(true);

  // API counts container
  const [counts, setCounts] = useState<{ total?: number; read?: number; unread?: number; filtered?: number }>({});

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // derived counts (local fallback)
  const unreadLocal = useMemo(() => notifications.filter((n) => n.is_read === 0).length, [notifications]);
  const readLocal = useMemo(() => notifications.filter((n) => n.is_read === 1).length, [notifications]);

  const displayUnread = typeof counts.unread === "number" ? counts.unread : unreadLocal;
  const displayRead = typeof counts.read === "number" ? counts.read : readLocal;
  const displayTotal = typeof counts.total === "number" ? counts.total : total;

  async function fetchNotifications(opts?: { page?: number; status?: "all" | "unread" | "read" }) {
    const p = opts?.page ?? page;
    const s = opts?.status ?? statusFilter;
    setLoading(true);
    setError(null);

    try {
      const urlBase = s === "all" ? "/notifications" : `/notifications?status=${s}`;
      // append pagination params (adjust names if your API is different)
      const url = `${urlBase}${urlBase.includes("?") ? "&" : "?"}page=${p}&per_page=${perPage}`;

      const res = await axiosClient.get<ApiResponse>(url);
      if (!mounted.current) return;

      // populate notifications
      setNotifications(res.data.notifications || []);

      // populate counts if returned by API
      setCounts({
        total: res.data.total_notifications,
        read: res.data.read_count,
        unread: res.data.unread_count,
        filtered: res.data.filtered_count,
      });

      // fallback: if API returned legacy count field
      if (typeof res.data.count === "number") {
        setTotal(res.data.count);
      } else if (typeof res.data.total_notifications === "number") {
        setTotal(res.data.total_notifications);
      } else {
        setTotal(null);
      }
    } catch (err: any) {
      if (!mounted.current) return;
      setError(err?.message ?? "Failed to load notifications");
    } finally {
      if (!mounted.current) return;
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchNotifications({ page, status: statusFilter });
  }, [statusFilter, page]);

  // optimistic mark single as read
  async function markAsRead(id: number) {
    const prev = notifications;
    const prevCounts = { ...counts };

    setNotifications((prevN) => prevN.map((n) => (n.notification_id === id ? { ...n, is_read: 1 } : n)));
    setCounts((c) => ({ ...c, unread: typeof c.unread === "number" ? Math.max(0, c.unread - 1) : c.unread }));

    try {
      await axiosClient.patch(`/notifications/${id}/read`);
    } catch (err: any) {
      setNotifications(prev);
      setCounts(prevCounts);
      setError(err?.message ?? "Failed to mark notification as read");
      fetchNotifications({ page, status: statusFilter });
    }
  }

  // mark all visible as read (optimistic)
  async function markAllVisibleAsRead() {
    const unreadIds = notifications.filter((n) => n.is_read === 0).map((n) => n.notification_id);
    if (unreadIds.length === 0) return;

    const prev = notifications;
    const prevCounts = { ...counts };

    setNotifications((prevN) => prevN.map((n) => ({ ...n, is_read: 1 })));
    setCounts((c) => ({ ...c, unread: 0 }));

    try {
      await Promise.all(unreadIds.map((id) => axiosClient.patch(`/notifications/${id}/read`)));
      // refresh counts/list to sync with server truth
      fetchNotifications({ page, status: statusFilter });
    } catch (err: any) {
      setNotifications(prev);
      setCounts(prevCounts);
      setError(err?.message ?? "Failed to mark some notifications as read");
      fetchNotifications({ page, status: statusFilter });
    }
  }

  function handlePrev() {
    setPage((p) => Math.max(1, p - 1));
  }
  function handleNext() {
    setPage((p) => p + 1);
  }

  return (
    <div className="pt-3 pb-8">
      <div className="mx-auto bg-white rounded-3xl shadow-xl p-4 md:p-6 ">
        <div className="flex items-center justify-between mb-4 gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Notifications</h1>
            <p className="text-sm text-gray-500 mt-1">Manage and review recent account activity</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Unread: <span className="font-medium">{displayUnread}</span>
            </div>
            {/* <Link to="/notifications/settings" className="text-sm text-blue-600 hover:underline hidden md:inline">
              Settings
            </Link> */}
          </div>
        </div>

        {/* Controls */}
        <div className="p-3 flex items-center gap-3 rounded-lg shadow-sm border border-gray-200 overflow-x-auto text-nowrap">
          <div className="flex gap-2">
            {(["all", "unread", "read"] as const).map((t) => {
              const label = t === "all" ? `All${displayTotal ? ` (${displayTotal})` : ""}` : t === "unread" ? `Unread${typeof counts.unread === "number" ? ` (${counts.unread})` : ""}` : `Read${typeof counts.read === "number" ? ` (${counts.read})` : ""}`;
              return (
                <button
                  key={t}
                  onClick={() => {
                    setStatusFilter(t);
                    setPage(1);
                  }}
                  className={`px-3 py-1 rounded text-sm ${statusFilter === t ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => fetchNotifications({ page: 1, status: statusFilter })}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
            >
              Refresh
            </button>

            <button
              onClick={markAllVisibleAsRead}
              className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 whitespace-nowrap"
            >
              Mark visible as read
            </button>
          </div>
        </div>

        <div className="mt-4 p-3">
          {loading && (
            <div className="space-y-3">
              {[1, 2, 3 , 4, 5, 6].map((i) => (
                <div key={i} className="flex items-start gap-4 p-3 rounded-lg shadow-sm border border-gray-100 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-gray-100" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-3/4" />
                    <div className="h-2 bg-gray-100 rounded w-2/3 mt-2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && <div className="text-sm text-red-500 mb-2">Error: {error}</div>}

          {!loading && notifications.length === 0 && !error && (
            <div className="text-sm text-gray-600">No notifications</div>
          )}

          <div className="divide-y">
            {!loading &&
              notifications.map((n) => (
                <div
                  key={n.notification_id}
                  className={`flex items-start gap-4 p-3 my-3 rounded-lg shadow-sm border ${n.is_read === 0 ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100"}`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <Icon icon={typeIcon(n.type)} width="20" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-800 truncate">{n.type}</div>
                          {n.is_read === 0 && (
                            <span className="ml-2 inline-block px-1 text-xs bg-red-500 text-white rounded">new</span>
                          )}
                        </div>

                        <div className="text-sm text-gray-700 mt-1 ">{n.message}</div>
                        <div className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</div>
                      </div>

                      <div className="ml-4 flex-shrink-0 text-right">
                        {n.is_read === 0 ? (
                          <button
                            onClick={() => markAsRead(n.notification_id)}
                            className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            Mark read
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Read</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* pagination footer */}
        <div className="mt-3 p-3 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-sm text-gray-600">
          <div>
            Showing <span className="font-medium">{notifications.length}</span> notifications
            {displayTotal !== null && <span className="ml-2">(Total: {displayTotal})</span>}
          </div>

          {/* <div className="flex items-center gap-2">
            <button onClick={handlePrev} disabled={page === 1} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
              Prev
            </button>
            <div>
              Page <span className="font-medium">{page}</span>
            </div>
            <button onClick={handleNext} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200">
              Next
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
}
